<script>
    import { onMount, onDestroy } from "svelte";
    import Select from "svelte-select/Select.svelte";

    import {
        checkProviderNumberValidation,
        importGoogleMapScript,
        removeGoogleMapScript,
    } from "../../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { isMobile } from "../../../stores";

    export let personalDetails = {};
    export let updateProviderDetailsFormPhase = () => {};

    let primaryProviderDetails = {
            isDefault: true,
            doctorId: personalDetails.createdUserId,
            isPreferredDefence: false,
            isPortingAcross: false,
            isError: false,
            // number: "988939BJ",
        },
        primaryLocationAddress,
        secondaryProviderDetails = {
            isDefault: false,
            doctorId: personalDetails.createdUserId,
            isPreferredDefence: false,
            isPortingAcross: false,
            isError: false,
            // number: "988939BJ",
        },
        secondaryLocationAddress,
        hospitalDetails = {};

    let isShowSecondaryProviderDetails = false,
        isShowRemoveSecondaryProviderConfirmationModal = false;

    $: isValidPrimaryProviderNumber = checkProviderNumberValidation(
        primaryProviderDetails.number,
    );

    $: isValidSecondaryProviderNumber = checkProviderNumberValidation(
        secondaryProviderDetails.number,
    );

    const checkAndAutoPadPrimaryProviderNumber = () => {
        if (
            primaryProviderDetails.number &&
            primaryProviderDetails.number.length < 8 &&
            !isValidPrimaryProviderNumber
        ) {
            primaryProviderDetails.number =
                primaryProviderDetails.number.padStart(8, "0");
        }
    };

    const checkAndAutoPadSecondaryProviderNumber = () => {
        if (
            secondaryProviderDetails.number &&
            secondaryProviderDetails.number.length < 8 &&
            !isValidSecondaryProviderNumber
        ) {
            secondaryProviderDetails.number =
                secondaryProviderDetails.number.padStart(8, "0");
        }
    };

    onMount(async () => {
        importGoogleMapScript();

        if (personalDetails.providers && personalDetails.providers.length)
            await _updateProviderDetails();
    });

    onDestroy(() => {
        removeGoogleMapScript();
    });

    const updateProviderDetails = () => {
        if (!_checkFieldsValidation()) return;

        _syncProviderDetailsBeforeMovingNext();

        updateProviderDetailsFormPhase(3);
    };

    const loadLocationAddress = async (searchText) => {
        if (!searchText) return [];

        let res = await apiMethods.getMethod.getAddressFromGoogle(searchText);

        if (res && res.predictions) return [...res.predictions];
    };

    const clearSelectedPrimaryLocation = () => {
        primaryLocationAddress = null;

        _resetDefaultStyle("PrimaryLocationInput");
    };

    const clearSelectedSecondaryLocation = () => {
        secondaryLocationAddress = null;

        _resetDefaultStyle("SecondaryLocationInput");
    };

    const handleSelectPrimaryLocation = async () => {
        await apiMethods.getMethod
            .getPlaceDetailsFromGoogle(primaryLocationAddress.place_id)
            .then((res) => {
                _syncLocationDetails(res, true);

                _resetDefaultStyle("primaryLocationAddressInput");
            });
    };

    const handleSelectSecondaryLocation = async () => {
        await apiMethods.getMethod
            .getPlaceDetailsFromGoogle(secondaryLocationAddress.place_id)
            .then((res) => {
                _syncLocationDetails(res, false);

                _resetDefaultStyle("secondaryLocationAddressInput");
            });
    };

    const loadHospitals = async (searchText) => {
        if (!searchText) return [];

        const res = await apiMethods.getMethod.getHospitals(0, 999999, {
            search: searchText,
        });

        if (!res || res.error) return [];

        return [...res.results];
    };

    const getOptionLabel = (option) => option.name;

    const clearSelectedHospital = (isPrimary) => {
        if (isPrimary) {
            hospitalDetails.primary = null;
            _resetDefaultStyle("PrimaryHospitalInput");
        } else {
            hospitalDetails.secondary = null;
            _resetDefaultStyle("SecondaryHospitalInput");
        }
    };

    const handleHospitalChange = (isPrimary) => {
        if (isPrimary) {
            if (!primaryProviderDetails.locationAddress) {
                primaryProviderDetails.locationAddress =
                    hospitalDetails.primary?.address ?? "";
                _resetDefaultStyle("primaryLocationAddressInput");
            }
        } else if (!secondaryProviderDetails.locationAddress) {
            secondaryProviderDetails.locationAddress =
                hospitalDetails.secondary?.address ?? "";
            _resetDefaultStyle("primaryLocationAddressInput");
        }
    };

    const addSecondaryProvider = () => {
        isShowSecondaryProviderDetails = true;
    };

    const removeSecondaryProvider = () => {
        isShowSecondaryProviderDetails = false;

        secondaryProviderDetails = {
            isDefault: false,
            doctorId: personalDetails.createdUserId,
            isPreferredDefence: false,
        };
    };

    const showRemoveSecondaryProviderConfirmationModal = () => {
        isShowRemoveSecondaryProviderConfirmationModal = true;
    };

    const closeRemoveSecondaryProviderConfirmationModal = () => {
        isShowRemoveSecondaryProviderConfirmationModal = false;
    };

    const confirmRemoveSecondaryProvider = () => {
        removeSecondaryProvider();

        closeRemoveSecondaryProviderConfirmationModal();
    };

    // HELPER FUNCTIONS
    const _checkFieldsValidation = () => {
        _updateInvalidFieldsStyle();

        return (
            primaryProviderDetails.number &&
            isValidPrimaryProviderNumber &&
            primaryLocationAddress?.place_id &&
            primaryProviderDetails.locationAddress &&
            hospitalDetails.primary?.id &&
            (isShowSecondaryProviderDetails
                ? secondaryProviderDetails.number &&
                  isValidSecondaryProviderNumber &&
                  secondaryLocationAddress?.place_id &&
                  secondaryProviderDetails.locationAddress &&
                  hospitalDetails.secondary?.id
                : true)
        );
    };

    const _updateInvalidFieldsStyle = () => {
        if (!primaryProviderDetails.number || !isValidPrimaryProviderNumber)
            _setErrorStyle("primaryProviderNumberInput");
        else _resetDefaultStyle("primaryProviderNumberInput");

        if (!primaryLocationAddress?.place_id)
            _setErrorStyle("PrimaryLocationInput");
        else _resetDefaultStyle("PrimaryLocationInput");

        if (!primaryProviderDetails.locationAddress)
            _setErrorStyle("primaryLocationAddressInput");
        else _resetDefaultStyle("primaryLocationAddressInput");

        if (!hospitalDetails.primary?.id)
            _setErrorStyle("PrimaryHospitalInput");
        else _resetDefaultStyle("PrimaryHospitalInput");

        if (isShowSecondaryProviderDetails) {
            if (
                !secondaryProviderDetails.number ||
                !isValidSecondaryProviderNumber
            )
                _setErrorStyle("secondaryProviderNumberInput");
            else _resetDefaultStyle("secondaryProviderNumberInput");

            if (!secondaryLocationAddress?.place_id)
                _setErrorStyle("SecondaryLocationInput");
            else _resetDefaultStyle("SecondaryLocationInput");

            if (!secondaryProviderDetails.locationAddress)
                _setErrorStyle("secondaryLocationAddressInput");
            else _resetDefaultStyle("secondaryLocationAddressInput");

            if (!hospitalDetails.secondary?.id)
                _setErrorStyle("SecondaryHospitalInput");
            else _resetDefaultStyle("SecondaryHospitalInput");
        }
    };

    const _updateProviderDetails = async () => {
        const { providers } = personalDetails;

        if (providers.some((provider) => provider.isDefault)) {
            primaryProviderDetails = providers.find(
                (provider) => provider.isDefault,
            );

            const locationRes = await apiMethods.getMethod.getAddressFromGoogle(
                primaryProviderDetails.location,
            );

            if (locationRes.predictions && locationRes.predictions.length)
                primaryLocationAddress = locationRes.predictions[0];

            const hospitalRes = await apiMethods.getMethod.getHospitals(0, 1, {
                search: primaryProviderDetails.hospitals[0].hospitalName,
            });

            if (hospitalRes.results)
                hospitalDetails.primary = { ...hospitalRes.results[0] };
        }

        if (providers.some((provider) => !provider.isDefault)) {
            isShowSecondaryProviderDetails = true;

            secondaryProviderDetails = providers.find(
                (provider) => !provider.isDefault,
            );

            const locationRes = await apiMethods.getMethod.getAddressFromGoogle(
                secondaryProviderDetails.location,
            );

            if (locationRes.predictions && locationRes.predictions.length)
                secondaryLocationAddress = locationRes.predictions[0];

            const hospitalRes = await apiMethods.getMethod.getHospitals(0, 1, {
                search: secondaryProviderDetails.hospitals[0].hospitalName,
            });

            if (hospitalRes.results)
                hospitalDetails.secondary = { ...hospitalRes.results[0] };
        }
    };

    const _syncLocationDetails = (details, isPrimary = true) => {
        const providerDetails = isPrimary
            ? { ...primaryProviderDetails }
            : { ...secondaryProviderDetails };

        providerDetails.location = isPrimary
            ? primaryLocationAddress.description
            : secondaryLocationAddress.description;

        if (!providerDetails.locationAddress)
            providerDetails.locationAddress = details.formatted_address;

        if (details.address_components)
            for (let component of details.address_components) {
                let types = component["types"];

                if (types.includes("locality"))
                    providerDetails.city = component["long_name"];
                else if (types.includes("country"))
                    providerDetails.country = component["long_name"];
                else if (types.includes("postal_code"))
                    providerDetails.postCode = component["long_name"];
                else if (types.includes("street_number"))
                    providerDetails.streetNumber = component["long_name"];
                else if (types.includes("route"))
                    providerDetails.streetName = component["long_name"];
                else if (types.includes("administrative_area_level_1"))
                    providerDetails.state = component["long_name"];
            }

        if (isPrimary) primaryProviderDetails = { ...providerDetails };
        else secondaryProviderDetails = { ...providerDetails };
    };

    const _syncProviderDetailsBeforeMovingNext = () => {
        _syncProviderDetails(true);
        if (isShowSecondaryProviderDetails) _syncProviderDetails(false);

        const providers = [{ ...primaryProviderDetails }];

        if (isShowSecondaryProviderDetails)
            providers.push({ ...secondaryProviderDetails });

        personalDetails.providers = [...providers];
    };

    const _syncProviderDetails = (isPrimary = true) => {
        const providerDetails = isPrimary
            ? { ...primaryProviderDetails }
            : { ...secondaryProviderDetails };

        const hospital = isPrimary
            ? { ...hospitalDetails.primary }
            : { ...hospitalDetails.secondary };

        providerDetails.hospitals = [
            {
                hospitalId: hospital.id,
                hospitalName: hospital.name,
                pipVendorNumber: "",
            },
        ];

        if (!providerDetails.hasOwnProperty("isNoGapBupa"))
            providerDetails.isNoGapBupa = true;

        if (!providerDetails.hasOwnProperty("isNoGapHCF"))
            providerDetails.isNoGapHCF = true;

        if (
            !providerDetails.isPortingAcross ||
            !providerDetails.hasOwnProperty("isNoGapNib")
        )
            providerDetails.isNoGapNib = true;

        if (
            providerDetails.isNoGapBupa ||
            providerDetails.isNoGapHCF ||
            providerDetails.isNoGapNib
        )
            providerDetails.isNoGap = true;
        else providerDetails.isNoGap = false;

        if (isPrimary) primaryProviderDetails = { ...providerDetails };
        else secondaryProviderDetails = { ...providerDetails };
    };

    const _setErrorStyle = (id) => {
        const field = document.getElementById(`${id}`);
        field.style.outline = "red solid 2px";

        if (id === "primaryProviderNumberInput")
            primaryProviderDetails.isError = true;

        if (id === "secondaryProviderNumberInput")
            secondaryProviderDetails.isError = true;
    };

    const _resetDefaultStyle = (id) => {
        const field = document.getElementById(`${id}`);
        field.style.outline = "none";

        if (id === "primaryProviderNumberInput")
            primaryProviderDetails.isError = false;

        if (id === "secondaryProviderNumberInput")
            secondaryProviderDetails.isError = false;
    };
</script>

<form
    class="flex flex-col gap-y-4 w-full"
    on:submit|preventDefault={updateProviderDetails}
>
    <p class="text-[16px] text-gray-600 w-full">
        You need at least one unique provider number of Anaemate. You can create
        a new provider number or port over an existing provider number.
    </p>

    <div class="divider" />

    <div class="flex flex-col max-w-full p-4 bg-gray-200 rounded-lg gap-y-4">
        <p class="text-[20px] font-bold">Primary Provider Number</p>

        <!-- Primary Provider Number -->
        <!-- Sample - 988939BJ -->
        <div class="flex flex-col gap-y-2">
            <label
                for="primaryProviderNumber"
                class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
            >
                Provider number
            </label>

            <input
                type="text"
                class="input input-sm"
                name="primaryProviderNumber"
                id="primaryProviderNumberInput"
                autocomplete="off"
                bind:value={primaryProviderDetails.number}
                on:focus={() =>
                    _resetDefaultStyle("primaryProviderNumberInput")}
                on:blur={checkAndAutoPadPrimaryProviderNumber}
            />

            {#if primaryProviderDetails.isError}
                <p class="text-[16px] text-error">
                    Please provide a valid provider number
                </p>
            {/if}

            <div class="flex items-center gap-x-4">
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={primaryProviderDetails.isPortingAcross}
                />
                <p class="text-[16px] text-gray-600">
                    This provider number has previously been registered with
                    health funds.
                </p>
            </div>
        </div>

        <div class="divider" />

        <!-- Primary Location Name -->
        <div class="flex flex-col gap-y-2 w-full">
            <label
                for="primaryLocationName"
                class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
            >
                Location name
            </label>

            <div class="flex max-w-full" id="PrimaryLocationInput">
                <Select
                    class="rounded-full"
                    bind:value={primaryLocationAddress}
                    loadOptions={loadLocationAddress}
                    itemId="place_id"
                    label="description"
                    on:clear={clearSelectedPrimaryLocation}
                    on:select={handleSelectPrimaryLocation}
                    noOptionsMessage="No available locations found"
                    placeholder="Type and select location"
                />
            </div>
        </div>

        <!-- Primary Location Address -->
        <div class="flex flex-col gap-y-2">
            <label
                for="primaryLocationAddress"
                class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
            >
                Location Address
            </label>

            <input
                type="text"
                class="input input-sm"
                name="primaryLocationAddress"
                id="primaryLocationAddressInput"
                bind:value={primaryProviderDetails.locationAddress}
                on:focus={() =>
                    _resetDefaultStyle("primaryLocationAddressInput")}
            />
        </div>

        <!-- Primary Hospital -->
        <div class="flex flex-col gap-y-2">
            <label
                for="primaryHospital"
                class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
            >
                Link provider number to a hospital
            </label>

            <div class="flex max-w-full" id="PrimaryHospitalInput">
                <Select
                    class="rounded-full"
                    bind:value={hospitalDetails.primary}
                    loadOptions={loadHospitals}
                    itemId="id"
                    label="name"
                    on:clear={() => clearSelectedHospital(true)}
                    on:select={() => handleHospitalChange(true)}
                    noOptionsMessage="No hospitals found"
                    placeholder="Type and select hospital"
                />
            </div>
        </div>

        <div class="divider" />

        <p class="text-[20px] font-bold">Scheme selection</p>

        <!-- Primary Gap Fee Option -->
        <p class="text-[16px] text-gray-600">
            {primaryProviderDetails.isPortingAcross
                ? "Please select the scheme you have previously signed up for."
                : "Select Known Gap if you want to be able to bill a gap. Leave Defence provider unchecked if unsure."}
        </p>

        <div class="flex flex-col gap-y-2 px-4">
            <div
                class="flex items-center justify-between w-full {$isMobile
                    ? 'flex-col'
                    : 'flex-row'}"
            >
                <p class="text-[16px] text-gray-600 w-48">BUPA</p>

                <div class="flex gap-x-4 w-full items-center justify-between">
                    <button
                        class={`btn btn-md rounded-full ${
                            primaryProviderDetails.hasOwnProperty("isNoGapBupa")
                                ? primaryProviderDetails.isNoGapBupa
                                    ? "bg-gray-600 text-[#fff]"
                                    : "bg-[#fff] text-gray-600"
                                : ""
                        }`}
                        on:click|preventDefault={() =>
                            (primaryProviderDetails.isNoGapBupa = true)}
                    >
                        No Gap
                    </button>
                    <button
                        class={`btn btn-md rounded-full ${
                            primaryProviderDetails.hasOwnProperty("isNoGapBupa")
                                ? primaryProviderDetails.isNoGapBupa
                                    ? "bg-[#fff] text-gray-600"
                                    : "bg-gray-600 text-[#fff]"
                                : ""
                        }`}
                        on:click|preventDefault={() =>
                            (primaryProviderDetails.isNoGapBupa = false)}
                    >
                        Known Gap
                    </button>
                </div>
            </div>

            <div
                class="flex items-center justify-between w-full {$isMobile
                    ? 'flex-col'
                    : 'flex-row'}"
            >
                <p class="text-[16px] text-gray-600 w-48">HCF</p>

                <div class="flex gap-x-4 w-full items-center justify-between">
                    <button
                        class={`btn btn-md rounded-full ${
                            primaryProviderDetails.hasOwnProperty("isNoGapHCF")
                                ? primaryProviderDetails.isNoGapHCF
                                    ? "bg-gray-600 text-[#fff]"
                                    : "bg-[#fff] text-gray-600"
                                : ""
                        }`}
                        on:click|preventDefault={() =>
                            (primaryProviderDetails.isNoGapHCF = true)}
                    >
                        No Gap
                    </button>
                    <button
                        class={`btn btn-md rounded-full ${
                            primaryProviderDetails.hasOwnProperty("isNoGapHCF")
                                ? primaryProviderDetails.isNoGapHCF
                                    ? "bg-[#fff] text-gray-600"
                                    : "bg-gray-600 text-[#fff]"
                                : ""
                        }`}
                        on:click|preventDefault={() =>
                            (primaryProviderDetails.isNoGapHCF = false)}
                    >
                        Known Gap
                    </button>
                </div>
            </div>

            {#if primaryProviderDetails.isPortingAcross}
                <div
                    class="flex items-center justify-between w-full {$isMobile
                        ? 'flex-col'
                        : 'flex-row'}"
                >
                    <p class="text-[16px] text-gray-600 w-48">NIB</p>

                    <div
                        class="flex gap-x-4 w-full items-center justify-between"
                    >
                        <button
                            class={`btn btn-md rounded-full ${
                                primaryProviderDetails.hasOwnProperty(
                                    "isNoGapNib",
                                )
                                    ? primaryProviderDetails.isNoGapNib
                                        ? "bg-gray-600 text-[#fff]"
                                        : "bg-[#fff] text-gray-600"
                                    : ""
                            }`}
                            on:click|preventDefault={() =>
                                (primaryProviderDetails.isNoGapNib = true)}
                        >
                            MediGap
                        </button>
                        <button
                            class={`btn btn-md rounded-full ${
                                primaryProviderDetails.hasOwnProperty(
                                    "isNoGapNib",
                                )
                                    ? primaryProviderDetails.isNoGapNib
                                        ? "bg-[#fff] text-gray-600"
                                        : "bg-gray-600 text-[#fff]"
                                    : ""
                            }`}
                            on:click|preventDefault={() =>
                                (primaryProviderDetails.isNoGapNib = false)}
                        >
                            Gap Sure
                        </button>
                    </div>
                </div>
            {/if}
        </div>

        <!-- Primary Defence Checkbox -->
        <div class="flex items-center gap-x-4">
            <input
                type="checkbox"
                class="toggle toggle-secondary"
                bind:checked={primaryProviderDetails.isPreferredDefence}
            />
            <p class="text-[16px] text-gray-600">
                Preferred provider for Australian Defence Force? (leave
                unchecked if unsure)
            </p>
        </div>
    </div>

    {#if !isShowSecondaryProviderDetails}
        <div class="flex items-center justify-center">
            <p
                class="text-[20px] text-secondary font-bold cursor-pointer"
                on:click|preventDefault={addSecondaryProvider}
            >
                Add another provider number
            </p>
        </div>
    {:else}
        <div class="flex flex-col w-full p-4 bg-gray-200 rounded-lg gap-y-4">
            <div class="flex items-center justify-between">
                <p class="text-[20px] font-bold">Secondary Provider Number</p>
                <p
                    class="text-[16px] font-bold text-error cursor-pointer"
                    on:click|preventDefault={showRemoveSecondaryProviderConfirmationModal}
                >
                    Remove
                </p>
            </div>

            <!-- Secondary Provider Number -->
            <div class="flex flex-col gap-y-2">
                <label
                    for="secondaryProviderNumber"
                    class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
                >
                    Provider number
                </label>

                <input
                    type="text"
                    class="input input-sm"
                    name="secondaryProviderNumber"
                    id="secondaryProviderNumberInput"
                    autocomplete="off"
                    bind:value={secondaryProviderDetails.number}
                    on:focus={() =>
                        _resetDefaultStyle("secondaryProviderNumberInput")}
                    on:blur={checkAndAutoPadSecondaryProviderNumber}
                />

                {#if secondaryProviderDetails.isError}
                    <p class="text-[16px] text-error">
                        Please provide a valid provider number
                    </p>
                {/if}

                <div class="flex items-center gap-x-4">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={secondaryProviderDetails.isPortingAcross}
                    />
                    <p class="text-[16px] text-gray-600">
                        This provider number has previously been registered with
                        health funds.
                    </p>
                </div>
            </div>

            <div class="divider" />

            <!-- Secondary Location Name -->
            <div class="flex flex-col gap-y-2">
                <label
                    for="secondaryLocationName"
                    class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
                >
                    Location name
                </label>

                <div class="flex max-w-full" id="SecondaryLocationInput">
                    <Select
                        class="rounded-full"
                        bind:value={secondaryLocationAddress}
                        loadOptions={loadLocationAddress}
                        itemId="place_id"
                        label="description"
                        on:clear={clearSelectedSecondaryLocation}
                        on:select={handleSelectSecondaryLocation}
                        noOptionsMessage="No available locations found"
                        placeholder="Type and select location"
                    />
                </div>
            </div>

            <!-- Secondary Location Address -->
            <div class="flex flex-col gap-y-2">
                <label
                    for="secondaryLocationAddress"
                    class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
                >
                    Location Address
                </label>

                <input
                    type="text"
                    class="input input-sm"
                    name="secondaryLocationAddress"
                    id="secondaryLocationAddressInput"
                    bind:value={secondaryProviderDetails.locationAddress}
                    on:focus={() =>
                        _resetDefaultStyle("secondaryLocationAddressInput")}
                />
            </div>

            <!-- Secondary Hospital -->
            <div class="flex flex-col gap-y-2">
                <label
                    for="secondaryHospital"
                    class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
                >
                    Link provider number to a hospital
                </label>

                <div class="flex max-w-full" id="SecondaryHospitalInput">
                    <Select
                        class="rounded-full"
                        bind:value={hospitalDetails.secondary}
                        loadOptions={loadHospitals}
                        itemId="id"
                        label="name"
                        on:clear={() => clearSelectedHospital(false)}
                        on:select={() => handleHospitalChange(false)}
                        noOptionsMessage="No hospitals found"
                        placeholder="Type and select hospital"
                    />
                </div>
            </div>

            <div class="divider" />

            <p class="text-[20px] font-bold">Scheme selection</p>

            <!-- Secondary Gap Fee Option -->
            <p class="text-[16px] text-gray-600">
                {secondaryProviderDetails.isPortingAcross
                    ? "Please select the scheme you have previously signed up for."
                    : "Select Known Gap if you want to be able to bill a gap. Leave Defence provider unchecked if unsure."}
            </p>

            <div class="flex flex-col gap-y-2 px-4">
                <div
                    class="flex items-center justify-between w-full {$isMobile
                        ? 'flex-col'
                        : 'flex-row'}"
                >
                    <p class="text-[16px] text-gray-600 w-48">BUPA</p>

                    <div
                        class="flex gap-x-4 w-full items-center justify-between"
                    >
                        <button
                            class={`btn btn-md rounded-full ${
                                secondaryProviderDetails.hasOwnProperty(
                                    "isNoGapBupa",
                                )
                                    ? secondaryProviderDetails.isNoGapBupa
                                        ? "bg-gray-600 text-[#fff]"
                                        : "bg-[#fff] text-gray-600"
                                    : ""
                            }`}
                            on:click|preventDefault={() =>
                                (secondaryProviderDetails.isNoGapBupa = true)}
                        >
                            No Gap
                        </button>
                        <button
                            class={`btn btn-md rounded-full ${
                                secondaryProviderDetails.hasOwnProperty(
                                    "isNoGapBupa",
                                )
                                    ? secondaryProviderDetails.isNoGapBupa
                                        ? "bg-[#fff] text-gray-600"
                                        : "bg-gray-600 text-[#fff]"
                                    : ""
                            }`}
                            on:click|preventDefault={() =>
                                (secondaryProviderDetails.isNoGapBupa = false)}
                        >
                            Known Gap
                        </button>
                    </div>
                </div>

                <div
                    class="flex items-center justify-between w-full {$isMobile
                        ? 'flex-col'
                        : 'flex-row'}"
                >
                    <p class="text-[16px] text-gray-600 w-48">HCF</p>

                    <div
                        class="flex gap-x-4 w-full items-center justify-between"
                    >
                        <button
                            class={`btn btn-md rounded-full ${
                                secondaryProviderDetails.hasOwnProperty(
                                    "isNoGapHCF",
                                )
                                    ? secondaryProviderDetails.isNoGapHCF
                                        ? "bg-gray-600 text-[#fff]"
                                        : "bg-[#fff] text-gray-600"
                                    : ""
                            }`}
                            on:click|preventDefault={() =>
                                (secondaryProviderDetails.isNoGapHCF = true)}
                        >
                            No Gap
                        </button>
                        <button
                            class={`btn btn-md rounded-full ${
                                secondaryProviderDetails.hasOwnProperty(
                                    "isNoGapHCF",
                                )
                                    ? secondaryProviderDetails.isNoGapHCF
                                        ? "bg-[#fff] text-gray-600"
                                        : "bg-gray-600 text-[#fff]"
                                    : ""
                            }`}
                            on:click|preventDefault={() =>
                                (secondaryProviderDetails.isNoGapHCF = false)}
                        >
                            Known Gap
                        </button>
                    </div>
                </div>

                {#if secondaryProviderDetails.isPortingAcross}
                    <div
                        class="flex items-center justify-between w-full {$isMobile
                            ? 'flex-col'
                            : 'flex-row'}"
                    >
                        <p class="text-[16px] text-gray-600 w-48">NIB</p>

                        <div
                            class="flex gap-x-4 w-full items-center justify-between"
                        >
                            <button
                                class={`btn btn-md rounded-full ${
                                    secondaryProviderDetails.hasOwnProperty(
                                        "isNoGapNib",
                                    )
                                        ? secondaryProviderDetails.isNoGapNib
                                            ? "bg-gray-600 text-[#fff]"
                                            : "bg-[#fff] text-gray-600"
                                        : ""
                                }`}
                                on:click|preventDefault={() =>
                                    (secondaryProviderDetails.isNoGapNib = true)}
                            >
                                MediGap
                            </button>
                            <button
                                class={`btn btn-md rounded-full ${
                                    secondaryProviderDetails.hasOwnProperty(
                                        "isNoGapNib",
                                    )
                                        ? secondaryProviderDetails.isNoGapNib
                                            ? "bg-[#fff] text-gray-600"
                                            : "bg-gray-600 text-[#fff]"
                                        : ""
                                }`}
                                on:click|preventDefault={() =>
                                    (secondaryProviderDetails.isNoGapNib = false)}
                            >
                                Gap Sure
                            </button>
                        </div>
                    </div>
                {/if}
            </div>

            <!-- Secondary Defence Checkbox -->
            <div class="flex items-center gap-x-4">
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={secondaryProviderDetails.isPreferredDefence}
                />
                <p class="text-[16px] text-gray-600">
                    Preferred provider for Australian Defence Force? (leave
                    unchecked if unsure)
                </p>
            </div>
        </div>
    {/if}

    <div class="flex w-full">
        <button
            class="btn btn-sm btn-secondary w-full"
            on:click|preventDefault={updateProviderDetails}
        >
            Next
        </button>
    </div>
</form>

<div
    class={isShowRemoveSecondaryProviderConfirmationModal
        ? "modal modal-open sm:modal-middle"
        : "modal"}
>
    <div class="modal-box">
        <h3 class="font-medium text-lg">Warning</h3>
        <div class="p-4">
            <p class="leading-6 text-error">
                Remove secondary provider details? All
                data will be removed.
            </p>
        </div>
        <div class="modal-action">
            <button
                class="btn btn-error text-white btn-sm border-none w-24"
                on:click={confirmRemoveSecondaryProvider}
            >
                Remove
            </button>
            <button
                class="btn btn-secondary text-white btn-sm border-none w-24"
                on:click={closeRemoveSecondaryProviderConfirmationModal}
            >
                Cancel
            </button>
        </div>
    </div>
</div>

<style>
    .mandatory::before {
        content: " * ";
    }
</style>
